/**
 * Inspection Timer Hook
 * Manages timer sessions for workflow inspections.
 * Session-based: each pause/resume creates a new session row.
 * total_duration_seconds = sum of all completed session durations + current running session elapsed.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logTicketEventToOcis } from '@/utils/ocisIngest';
// DTC-F: route the inspection-timer live-elapsed counter and write
// timestamps (`stopped_at`, session-elapsed deltas) through the
// canonical clock so the timer state machine is deterministic under
// `clock.setFixed(...)`. Same drift class DTC-A fixed in the ticket-
// timer manager. Behavior identical at production wall-clock time.
import { clock } from '@/utils/clock';
import { pauseOption, type PauseDetail } from '@/lib/timer/interaction/pauseIntent';
import { toast } from 'sonner';

/**
 * W5 (field test 2026-10-16): ONE hands-on timer per person, across ticket AND inspection work. The server refuses
 * an inspection start/resume while a ticket timer (or another inspection) runs — HANDS_ON_TIMER_ACTIVE, with
 * nothing written. The refusal is reported, never logged as a start.
 */
export class HandsOnTimerActiveError extends Error {
  constructor(public readonly activeTicketId: string | null) {
    super('You already have hands-on work running. Pause or stop it before timing this inspection.');
    this.name = 'HandsOnTimerActiveError';
  }
}
const reportInspectionRefusal = (e: unknown) => {
  if (e instanceof HandsOnTimerActiveError) {
    toast.warning('You already have hands-on work running', {
      description: 'One hands-on timer runs at a time. Pause or stop the other work before timing this inspection.',
    });
  }
};

export type TimerStatus = 'running' | 'paused' | 'stopped' | null;

export interface InspectionTimerState {
  /** Live elapsed seconds (total_duration_seconds + current session elapsed) */
  elapsedSeconds: number;
  /** Current timer status from DB */
  timerStatus: TimerStatus;
  /** Book time from template (minutes) */
  bookTimeMin: number | null;
  /** Is any mutation in flight */
  isMutating: boolean;
  /** Start the timer (creates first session) */
  startTimer: (inspectionId: string) => void;
  /** Pause (closes current session) — a pause always carries a structured reason (CORE-AJ). */
  pauseTimer: (inspectionId: string, detail: PauseDetail) => void;
  /** Resume (creates new session) */
  resumeTimer: (inspectionId: string) => void;
  /** Stop (closes session, finalizes total) */
  stopTimer: (inspectionId: string) => void;
}

export function useInspectionTimer(
  inspectionId: string | null,
  ticketId: string | null,
  shopId: string | null
): InspectionTimerState {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id;
  const [liveElapsed, setLiveElapsed] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch inspection timer state
  const { data: inspectionData } = useQuery({
    queryKey: ['inspection-timer', inspectionId],
    queryFn: async () => {
      if (!inspectionId) return null;
      const { data } = await supabase
        .from('ticket_workflow_inspections')
        .select('timer_status, current_timer_session_id, book_time_min, total_duration_seconds')
        .eq('id', inspectionId)
        .single();
      return data as any;
    },
    enabled: !!inspectionId,
    refetchInterval: false,
  });

  // Fetch current running session start time (for live counter)
  const { data: currentSession } = useQuery({
    queryKey: ['inspection-timer-session', inspectionData?.current_timer_session_id],
    queryFn: async () => {
      if (!inspectionData?.current_timer_session_id) return null;
      const { data } = await supabase
        .from('workflow_inspection_timer_sessions' as any)
        .select('started_at')
        .eq('id', inspectionData.current_timer_session_id)
        .single();
      return data as any;
    },
    enabled: !!inspectionData?.current_timer_session_id && inspectionData?.timer_status === 'running',
  });

  const timerStatus = (inspectionData?.timer_status as TimerStatus) ?? null;
  const totalDuration = inspectionData?.total_duration_seconds ?? 0;
  const bookTimeMin = inspectionData?.book_time_min ?? null;

  // Live counter effect
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (timerStatus === 'running' && currentSession?.started_at) {
      const sessionStart = new Date(currentSession.started_at).getTime();
      const tick = () => {
        const sessionElapsed = Math.floor((clock.nowMs() - sessionStart) / 1000);
        setLiveElapsed(totalDuration + sessionElapsed);
      };
      tick();
      intervalRef.current = setInterval(tick, 1000);
    } else {
      setLiveElapsed(totalDuration);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [timerStatus, currentSession?.started_at, totalDuration]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['inspection-timer', inspectionId] });
    queryClient.invalidateQueries({ queryKey: ['inspection-timer-session'] });
    queryClient.invalidateQueries({ queryKey: ['workflow-inspections'] });
  }, [queryClient, inspectionId]);

  // Core-100 R4 (CORE-AJ): every transition goes through the ONE server authority, which measures on the SERVER
  // clock and requires a structured reason for a pause (inspection_timer_transition, migration 20270206014900).
  // The browser no longer writes sessions, durations or the timer state.
  const transition = async (iid: string, action: 'start' | 'pause' | 'resume' | 'stop', pauseDetail?: PauseDetail) => {
    if (!ticketId || !shopId || !userId) throw new Error('Missing context');
    const { data, error } = await supabase.rpc('inspection_timer_transition' as never, {
      p_inspection_id: iid, p_action: action, p_pause_detail: pauseDetail ?? null,
    } as never);
    if (error) throw error;
    const r = data as unknown as { outcome?: string; session_seconds?: number; total_duration_seconds?: number; active_ticket_id?: string | null } | null;
    if (r?.outcome === 'HANDS_ON_TIMER_ACTIVE') throw new HandsOnTimerActiveError(r.active_ticket_id ?? null);
    return r;
  };

  const startTimerMut = useMutation({
    mutationFn: async (iid: string) => {
      await transition(iid, 'start');
      logTicketEventToOcis({ ticketId: ticketId!, shopId: shopId!, eventType: 'inspection_timer_started' as any, content: 'Inspection timer started', authorUserId: userId });
    },
    onSuccess: invalidate,
    onError: reportInspectionRefusal,
  });

  const pauseTimerMut = useMutation({
    mutationFn: async ({ iid, detail }: { iid: string; detail: PauseDetail }) => {
      const r = await transition(iid, 'pause', detail);
      const label = pauseOption(detail)?.label ?? detail;
      logTicketEventToOcis({ ticketId: ticketId!, shopId: shopId!, eventType: 'inspection_timer_paused' as any,
        content: `Timer paused — ${label} (${Math.round((r?.session_seconds ?? 0) / 60)}m session)`, authorUserId: userId });
    },
    onSuccess: invalidate,
  });

  const resumeTimerMut = useMutation({
    mutationFn: async (iid: string) => {
      await transition(iid, 'resume');
      logTicketEventToOcis({ ticketId: ticketId!, shopId: shopId!, eventType: 'inspection_timer_resumed' as any, content: 'Timer resumed', authorUserId: userId });
    },
    onSuccess: invalidate,
    onError: reportInspectionRefusal,
  });

  const stopTimerMut = useMutation({
    mutationFn: async (iid: string) => {
      const r = await transition(iid, 'stop');
      logTicketEventToOcis({ ticketId: ticketId!, shopId: shopId!, eventType: 'inspection_timer_stopped' as any,
        content: `Timer stopped (total: ${Math.round((r?.total_duration_seconds ?? totalDuration) / 60)}m)`, authorUserId: userId });
    },
    onSuccess: invalidate,
  });

  return {
    elapsedSeconds: liveElapsed,
    timerStatus,
    bookTimeMin,
    isMutating: startTimerMut.isPending || pauseTimerMut.isPending || resumeTimerMut.isPending || stopTimerMut.isPending,
    startTimer: (iid) => startTimerMut.mutate(iid),
    pauseTimer: (iid, detail) => pauseTimerMut.mutate({ iid, detail }),
    resumeTimer: (iid) => resumeTimerMut.mutate(iid),
    stopTimer: (iid) => stopTimerMut.mutate(iid),
  };
}
